import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { Button, Input, Label, Card, CardContent, CardHeader, CardTitle } from '@erp/ui';

// Sends a Supabase password-recovery email. The redirect URL is the
// app's own /auth/update-password page on the current origin, so the
// user comes back to the same domain (tunielec.gesty.tn, localhost,
// etc.) after clicking the link in the email. Supabase rejects redirect
// URLs that aren't in the `uri_allow_list` auth config; the wildcard
// `https://*.gesty.tn` covers every tenant subdomain.
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const redirectTo = `${window.location.origin}/auth/update-password`;
      await requestPasswordReset(email.trim().toLowerCase(), redirectTo);
      setDone(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {t('auth.forgotTitle', 'Mot de passe oublié')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
                {t(
                  'auth.forgotSent',
                  'Email envoyé. Vérifiez votre boîte de réception et cliquez sur le lien pour réinitialiser votre mot de passe.',
                )}
              </div>
              <Link to="/login" className="block text-center text-sm text-primary hover:underline">
                {t('auth.backToLogin', 'Retour à la connexion')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t(
                  'auth.forgotSubtitle',
                  'Entrez votre adresse email pour recevoir un lien de réinitialisation.',
                )}
              </p>
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting
                  ? t('common.loading')
                  : t('auth.forgotSubmit', "Envoyer le lien")}
              </Button>
              <Link
                to="/login"
                className="block text-center text-sm text-muted-foreground hover:underline"
              >
                {t('auth.backToLogin', 'Retour à la connexion')}
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
